import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { ReplayButton } from "@/components/ReplayButton";

type Posture = {
  env: string;
  cookie_secure: boolean;
  smtp_enabled: boolean;
  allow_prod_seed: boolean;
  users_total: number;
  users_totp_enrolled: number;
  cloudflare_fronted: boolean;
  jwt_ttl_minutes: number;
  lockout_max_attempts: number;
  lockout_window_minutes: number;
  master_key_age_days_estimate: number | null;
};

type Stats = {
  totals: {
    real_keys: number;
    proxy_keys_total: number;
    proxy_keys_active: number;
    stale_real_keys: number;
    expiring_proxy_keys: number;
  };
  posture: Posture;
  last_24h: {
    requests: number;
    errors: number;
    error_rate: number;
    avg_latency_ms: number;
    bytes_down: number;
  };
  top_providers: { provider: string; requests: number }[];
  by_project: {
    project: string;
    key_count: number;
    requests_24h: number;
    errors_24h: number;
    error_rate_24h: number;
    avg_latency_ms: number;
    last_seen: string | null;
  }[];
  recent_errors: {
    id: number;
    ts: string | null;
    method: string;
    path: string;
    upstream_status: number | null;
    latency_ms: number | null;
    error: string | null;
    proxy_key_label: string | null;
    provider: string | null;
  }[];
  dormant_proxy_keys: {
    id: number;
    label: string;
    project: string | null;
    prefix: string;
    real_key_id: number;
    age_days: number | null;
  }[];
  stale_real_keys: {
    id: number;
    label: string;
    provider: string;
    rotated_at: string | null;
    days_since_rotation: number | null;
  }[];
  expiring_proxy_keys: {
    id: number;
    label: string;
    project: string | null;
    prefix: string;
    expires_at: string | null;
  }[];
  recent_audit: {
    id: number;
    ts: string | null;
    action: string;
    subject_type: string;
    subject_id: number | null;
    ip: string | null;
  }[];
  low_quota_real_keys: {
    id: number;
    label: string;
    provider: string;
    issuer_email: string | null;
    quota_amount: number;
    quota_used: number;
    quota_unit: string | null;
    quota_period: string | null;
    pct_used: number;
  }[];
};

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
}

function timeSince(iso: string | null): string {
  if (!iso) return "never";
  const sec = (Date.now() - new Date(iso).getTime()) / 1000;
  if (sec < 60) return `${Math.round(sec)}s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
  return `${Math.round(sec / 86400)}d ago`;
}

const REFRESH_MS = 10_000;

export function Dashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [ticking, setTicking] = useState(false);
  const [nowTick, setNowTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    const fetchNow = async () => {
      setTicking(true);
      try {
        const s = await api<Stats>("/api/admin/stats");
        if (!cancelled) {
          setStats(s);
          setUpdatedAt(Date.now());
          setErr(null);
        }
      } catch (e: any) {
        if (!cancelled) {
          setErr(e?.message ?? "load failed");
          // Audit 2026-05-21 P2: clear stale stats on error so operators
          // don't trust an old all-green snapshot during a backend outage.
          // The error message becomes the dashboard's primary signal.
          setStats(null);
        }
      } finally {
        if (!cancelled) setTicking(false);
      }
    };

    const schedule = () => {
      timer = window.setTimeout(async () => {
        if (document.visibilityState === "visible") await fetchNow();
        if (!cancelled) schedule();
      }, REFRESH_MS);
    };

    fetchNow();
    schedule();

    const onVis = () => {
      if (document.visibilityState === "visible") fetchNow();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // Tick a second-by-second counter for the "updated Ns ago" label
  useEffect(() => {
    const t = window.setInterval(() => setNowTick((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, []);

  const agoSeconds =
    updatedAt === null ? null : Math.max(0, Math.round((Date.now() - updatedAt) / 1000));
  const _ = nowTick; // force re-render on tick

  return (
    <div className="space-y-8">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl display">Dashboard</h1>
          <p className="text-sm text-slate-400">
            Posture, traffic, and attention items — auto-refreshes every 10s while this tab is open.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span
            className={`inline-block w-2 h-2 rounded-full ${
              ticking ? "bg-accent-500 animate-pulse" : "bg-emerald-500"
            }`}
          />
          {ticking
            ? "refreshing…"
            : agoSeconds === null
            ? "loading"
            : agoSeconds < 5
            ? "just now"
            : `updated ${agoSeconds}s ago`}
        </div>
      </header>

      {err && <div className="card text-rose-300 text-sm">{err}</div>}
      {!stats && !err && <div className="card text-slate-500 text-sm">Loading…</div>}

      {stats && (
        <>
          {/* ------- Top-line totals ------- */}
          <div className="grid grid-cols-5 gap-4">
            <Stat label="Real keys" value={stats.totals.real_keys} />
            <Stat label="Active proxy keys" value={stats.totals.proxy_keys_active} />
            <Stat
              label="Rotation overdue"
              value={stats.totals.stale_real_keys}
              tone={stats.totals.stale_real_keys > 0 ? "warn" : "ok"}
            />
            <Stat
              label="Expiring ≤ 7d"
              value={stats.totals.expiring_proxy_keys}
              tone={stats.totals.expiring_proxy_keys > 0 ? "warn" : "ok"}
            />
            <Stat
              label="Req errors 24h"
              value={stats.last_24h.errors}
              tone={stats.last_24h.errors > 0 ? "warn" : "ok"}
            />
          </div>

          {/* ------- Security posture ------- */}
          <PostureCard posture={stats.posture} />

          {/* ------- 24h traffic + top providers ------- */}
          <section className="grid grid-cols-[1fr_1.2fr] gap-6">
            <div className="card">
              <h2 className="font-semibold mb-3">Last 24 hours</h2>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <TrafficRow label="Requests" value={stats.last_24h.requests.toLocaleString()} />
                <TrafficRow
                  label="Error rate"
                  value={`${(stats.last_24h.error_rate * 100).toFixed(1)}%`}
                  tone={stats.last_24h.error_rate > 0.05 ? "warn" : "ok"}
                />
                <TrafficRow
                  label="Avg latency"
                  value={`${Math.round(stats.last_24h.avg_latency_ms)} ms`}
                />
                <TrafficRow label="Bytes down" value={fmtBytes(stats.last_24h.bytes_down)} />
              </div>
            </div>

            <div className="card">
              <h2 className="font-semibold mb-3">Top upstream providers (24h)</h2>
              {stats.top_providers.length === 0 ? (
                <p className="text-sm text-slate-500">No traffic yet.</p>
              ) : (
                <ProviderBars data={stats.top_providers} />
              )}
            </div>
          </section>

          {/* ------- By consumer project ------- */}
          <ByProjectCard data={stats.by_project} />

          {/* ------- Attention items ------- */}
          <section className="grid grid-cols-2 gap-6">
            <AttentionCard
              title="Rotation overdue"
              linkTo="/real-keys"
              empty="All real keys rotated within the policy window."
              items={stats.stale_real_keys.map((r) => ({
                id: r.id,
                text: `${r.label} (${r.provider})`,
                badge: `${r.days_since_rotation}d ago`,
                href: `/real-keys/${r.id}`,
              }))}
            />
            <AttentionCard
              title="Expiring proxy keys"
              linkTo="/proxy-keys"
              empty="Nothing expires in the next 7 days."
              items={stats.expiring_proxy_keys.map((p) => {
                const d = daysUntil(p.expires_at);
                return {
                  id: p.id,
                  text: `${p.label} (${p.project ?? "no project"})`,
                  badge: d !== null ? `in ${d}d` : "—",
                  href: `/proxy-keys/${p.id}`,
                };
              })}
            />
          </section>

          {/* ------- Low quota real keys ------- */}
          <LowQuotaCard items={stats.low_quota_real_keys} />

          {/* ------- Recent errors ------- */}
          <section className="card">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold">Recent upstream errors (24h)</h2>
              <span className="text-xs text-slate-500">
                status ≥ 400 or gateway rejection
              </span>
            </div>
            {stats.recent_errors.length === 0 ? (
              <p className="text-sm text-slate-500">No errors in the last 24h.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-slate-500 uppercase tracking-wide">
                  <tr>
                    <th className="py-2">Time</th>
                    <th>Method</th>
                    <th>Path</th>
                    <th>Status</th>
                    <th>Proxy key</th>
                    <th>Provider</th>
                    <th>Detail</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_errors.map((e) => (
                    <tr key={e.id} className="border-t border-white/5">
                      <td className="py-1.5 text-slate-400 whitespace-nowrap">
                        {e.ts ? new Date(e.ts).toLocaleTimeString() : "-"}
                      </td>
                      <td className="mono text-xs">{e.method}</td>
                      <td className="mono text-xs text-slate-400 truncate max-w-[280px]">
                        {e.path}
                      </td>
                      <td>
                        <span
                          className={
                            e.upstream_status && e.upstream_status >= 500
                              ? "badge-err"
                              : "badge-warn"
                          }
                        >
                          {e.upstream_status ?? "—"}
                        </span>
                      </td>
                      <td className="text-slate-400">{e.proxy_key_label ?? "—"}</td>
                      <td className="text-slate-500 text-xs">{e.provider ?? "—"}</td>
                      <td className="text-slate-500 text-xs truncate max-w-[200px]">
                        {e.error ?? ""}
                      </td>
                      <td className="pr-2">
                        <ReplayButton method={e.method} path={e.path} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {/* ------- Dormant keys ------- */}
          <section className="card">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold">Dormant proxy keys (0 requests in 30 days)</h2>
              <span className="text-xs text-slate-500">
                delete candidates
              </span>
            </div>
            {stats.dormant_proxy_keys.length === 0 ? (
              <p className="text-sm text-slate-500">No dormant keys — every active key saw traffic.</p>
            ) : (
              <ul className="divide-y divide-white/5 text-sm">
                {stats.dormant_proxy_keys.map((p) => (
                  <li key={p.id} className="py-2 flex items-center justify-between">
                    <Link
                      to={`/proxy-keys/${p.id}`}
                      className="hover:text-accent-500"
                    >
                      {p.label}{" "}
                      <span className="text-slate-500 mono text-xs">
                        ({p.prefix}… · project={p.project ?? "—"})
                      </span>
                    </Link>
                    <span className="text-xs text-slate-500">
                      age {p.age_days ?? "?"}d
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* ------- Recent audit ------- */}
          <section className="card">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold">Recent admin activity</h2>
              <Link to="/audit" className="text-xs text-accent-500 hover:underline">
                View all →
              </Link>
            </div>
            {stats.recent_audit.length === 0 ? (
              <p className="text-sm text-slate-500">No events yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-slate-500 uppercase tracking-wide">
                  <tr>
                    <th className="py-2">Time</th>
                    <th>Action</th>
                    <th>Subject</th>
                    <th>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_audit.map((a) => (
                    <tr key={a.id} className="border-t border-white/5">
                      <td className="py-2 text-slate-400">
                        {a.ts ? new Date(a.ts).toLocaleString() : "-"}
                      </td>
                      <td className="mono text-xs">{a.action}</td>
                      <td className="text-slate-400">
                        {a.subject_type}#{a.subject_id}
                      </td>
                      <td className="text-slate-500 mono text-xs">{a.ip ?? "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | string;
  tone?: "ok" | "warn";
}) {
  return (
    <div className="card py-5">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div
        className={`text-3xl font-semibold mt-1 ${
          tone === "warn" ? "text-amber-300" : "text-slate-100"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function PostureCard({ posture }: { posture: Posture }) {
  const items: { label: string; good: boolean; detail: string }[] = [
    {
      label: "Seed bypass off",
      good: !posture.allow_prod_seed,
      detail: posture.allow_prod_seed ? "ALLOW_PROD_SEED is still true!" : "prod seed guard active",
    },
    {
      label: "Cookie: secure",
      good: posture.cookie_secure,
      detail: posture.cookie_secure ? "HTTPS-only cookies" : "cookie_secure=false — set true behind TLS",
    },
    {
      label: "Cloudflare fronted",
      good: posture.cloudflare_fronted,
      detail: posture.cloudflare_fronted
        ? "trusted CF CIDRs in TRUSTED_PROXIES"
        : "direct Railway access possible — review TRUSTED_PROXIES",
    },
    {
      label: "SMTP alerts",
      good: posture.smtp_enabled,
      detail: posture.smtp_enabled ? "daily + drill emails live" : "SMTP_ENABLED=false — no emails sent",
    },
    {
      label: "Admins w/ TOTP",
      good: posture.users_total > 0 && posture.users_totp_enrolled === posture.users_total,
      detail: `${posture.users_totp_enrolled}/${posture.users_total} admins enrolled`,
    },
    {
      label: "Master key age",
      good:
        posture.master_key_age_days_estimate === null ||
        posture.master_key_age_days_estimate < 180,
      detail:
        posture.master_key_age_days_estimate === null
          ? "unknown (set MASTER_KEY_ROTATED_AT env var after rotation)"
          : `${posture.master_key_age_days_estimate}d since last rotation`,
    },
    {
      label: "JWT TTL",
      good: posture.jwt_ttl_minutes <= 240,
      detail: `${posture.jwt_ttl_minutes} min`,
    },
  ];

  const badCount = items.filter((i) => !i.good).length;

  return (
    <section className="card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold">Security posture</h2>
        {badCount === 0 ? (
          <span className="badge-ok">all green</span>
        ) : (
          <span className="badge-warn">{badCount} to review</span>
        )}
      </div>
      <div className="grid grid-cols-4 gap-3">
        {items.map((it) => (
          <div
            key={it.label}
            className={`rounded-lg border p-3 ${
              it.good
                ? "border-emerald-500/20 bg-emerald-500/5"
                : "border-amber-500/25 bg-amber-500/5"
            }`}
          >
            <div className="text-xs text-slate-400">{it.label}</div>
            <div
              className={`text-sm font-medium mt-0.5 ${
                it.good ? "text-emerald-300" : "text-amber-300"
              }`}
            >
              {it.good ? "ok" : "review"}
            </div>
            <div className="text-[11px] text-slate-500 mt-1 leading-snug">{it.detail}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function TrafficRow({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "ok" | "warn";
}) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-slate-500">{label}</span>
      <span
        className={`text-lg font-semibold ${
          tone === "warn" ? "text-amber-300" : "text-slate-100"
        }`}
      >
        {value}
      </span>
    </div>
  );
}

function ProviderBars({ data }: { data: { provider: string; requests: number }[] }) {
  const max = Math.max(...data.map((d) => d.requests), 1);
  return (
    <ul className="space-y-2 text-sm">
      {data.map((d) => {
        const pct = (d.requests / max) * 100;
        return (
          <li key={d.provider} className="flex items-center gap-3">
            <span className="w-28 text-slate-400 truncate">{d.provider}</span>
            <div className="flex-1 h-5 bg-white/5 rounded overflow-hidden">
              <div
                className="h-full bg-accent-500/70"
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="w-16 text-right mono text-xs text-slate-300">
              {d.requests.toLocaleString()}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function ByProjectCard({
  data,
}: {
  data: Stats["by_project"];
}) {
  if (data.length === 0) {
    return (
      <section className="card">
        <h2 className="font-semibold mb-3">By consumer project</h2>
        <p className="text-sm text-slate-500">
          No proxy keys issued yet. Issue one from a real-key page to track project traffic here.
        </p>
      </section>
    );
  }
  const maxReq = Math.max(...data.map((d) => d.requests_24h), 1);
  return (
    <section className="card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold">By consumer project</h2>
        <span className="text-xs text-slate-500">last 24h · {data.length} project{data.length === 1 ? "" : "s"}</span>
      </div>
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-slate-500 uppercase tracking-wide">
          <tr>
            <th className="py-2">Project</th>
            <th>Keys</th>
            <th>24h requests</th>
            <th>Errors</th>
            <th>Avg latency</th>
            <th>Last seen</th>
          </tr>
        </thead>
        <tbody>
          {data.map((p) => {
            const pct = (p.requests_24h / maxReq) * 100;
            const errPct = p.error_rate_24h * 100;
            const errTone = errPct > 5 ? "text-rose-300" : errPct > 0 ? "text-amber-300" : "text-slate-500";
            return (
              <tr key={p.project} className="border-t border-white/5">
                <td className="py-2">
                  <Link
                    to={`/proxy-keys?project=${encodeURIComponent(p.project === "(no project)" ? "" : p.project)}`}
                    className="hover:text-accent-500 font-medium"
                  >
                    {p.project}
                  </Link>
                </td>
                <td className="text-slate-300 mono text-xs">{p.key_count}</td>
                <td>
                  <div className="flex items-center gap-2">
                    <span className="mono text-xs text-slate-200 w-12 text-right">
                      {p.requests_24h.toLocaleString()}
                    </span>
                    <div className="flex-1 h-1.5 bg-white/5 rounded overflow-hidden max-w-[120px]">
                      <div className="h-full bg-accent-500/70" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </td>
                <td className={`mono text-xs ${errTone}`}>
                  {p.errors_24h > 0 ? `${p.errors_24h} (${errPct.toFixed(1)}%)` : "0"}
                </td>
                <td className="text-slate-400 mono text-xs">
                  {p.avg_latency_ms > 0 ? `${Math.round(p.avg_latency_ms)} ms` : "—"}
                </td>
                <td className="text-slate-400 text-xs">{timeSince(p.last_seen)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function AttentionCard({
  title,
  linkTo,
  items,
  empty,
}: {
  title: string;
  linkTo: string;
  items: { id: number; text: string; badge: string; href: string }[];
  empty: string;
}) {
  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold">{title}</h2>
        <Link to={linkTo} className="text-xs text-accent-500 hover:underline">
          Manage →
        </Link>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-slate-500">{empty}</p>
      ) : (
        <ul className="space-y-2 text-sm">
          {items.map((i) => (
            <li key={i.id} className="flex items-center justify-between">
              <Link to={i.href} className="hover:text-accent-500 truncate">
                {i.text}
              </Link>
              <span className="badge-warn whitespace-nowrap">{i.badge}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function LowQuotaCard({
  items,
}: {
  items: Stats["low_quota_real_keys"];
}) {
  return (
    <section className="card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold">Low quota real keys</h2>
        <span className="text-xs text-slate-500">
          ≥ 70% used (operator-tracked)
        </span>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-slate-500">
          All real keys with a quota set are below 70% used. Set quotas on the
          real-key detail page so we can warn before traffic hits a $0 key.
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((r) => {
            const tone =
              r.pct_used >= 95
                ? "bg-rose-500"
                : r.pct_used >= 85
                ? "bg-amber-500"
                : "bg-amber-500/70";
            const remaining = Math.max(0, r.quota_amount - r.quota_used);
            return (
              <li
                key={r.id}
                className="rounded-lg border border-white/5 bg-white/[0.02] p-3 hover:bg-white/[0.04] transition-colors"
              >
                <div className="flex items-center justify-between gap-3">
                  <Link
                    to={`/real-keys/${r.id}`}
                    className="text-slate-100 hover:text-accent-500 truncate"
                  >
                    {r.label}{" "}
                    <span className="text-xs text-slate-500">({r.provider})</span>
                  </Link>
                  <span
                    className={
                      r.pct_used >= 95
                        ? "text-rose-300 mono text-xs whitespace-nowrap"
                        : "text-amber-300 mono text-xs whitespace-nowrap"
                    }
                  >
                    {r.pct_used.toFixed(1)}% used
                  </span>
                </div>
                <div className="mt-1.5 h-1.5 w-full rounded-full bg-white/[0.04] overflow-hidden">
                  <div
                    className={`h-full ${tone} transition-all`}
                    style={{ width: `${Math.min(100, r.pct_used)}%` }}
                  />
                </div>
                <div className="mt-1.5 flex items-center justify-between text-[11px] text-slate-500">
                  <span>
                    {r.quota_used.toLocaleString()} /{" "}
                    {r.quota_amount.toLocaleString()} {r.quota_unit ?? ""}
                    {r.quota_period ? ` · ${r.quota_period}` : ""}
                  </span>
                  <span>
                    {remaining.toLocaleString()} {r.quota_unit ?? ""} left
                    {r.issuer_email ? ` · ${r.issuer_email}` : ""}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
